import { AppContextState, AppContextType } from "src/AppContextProvider/type";
import { SUBSCRIPTION_STATUS } from "src/constants/constants";
import { FEATURE_FLAGS_ENUM } from "src/constants/featureFlag";
import { Countries, EuCountries } from "src/billing/constants";

export const isPixiTrialist = (context: AppContextType | AppContextState) => {
  const isTrialist =
    context.subscription?.status === SUBSCRIPTION_STATUS.IN_TRIAL;
  const isPixiEnabled = context.shouldShowFeature(
    FEATURE_FLAGS_ENUM.PIXI_TRIALIST,
  );
  return isTrialist && isPixiEnabled;
};

export const isTrialist = (context: AppContextType | AppContextState) => {
  return context.subscription?.status === SUBSCRIPTION_STATUS.IN_TRIAL;
};

type CountryConfig<T> = {
  eu: T;
  us: T;
  ca: T;
  gb: T;
  default: T;
};

// Check if the country is in the EU (excluding GB)
const isEUNonGBCountry = (countryCode: string) =>
  EuCountries.filter((c) => c !== "GB").includes(countryCode);

const getCountrySpecificConfg = <T extends unknown>(
  countryCode: string,
  config: CountryConfig<T>,
): T => {
  if (isEUNonGBCountry(countryCode)) {
    return config.eu;
  }

  switch (countryCode) {
    case Countries.US:
    case "US":
      return config.us;

    case Countries.CA:
    case "CA":
      return config.ca;

    case Countries.GB:
    case "GB":
      return config.gb;

    default:
      return config.default;
  }
};

type PriceInfo = {
  fullPrice: number;
  discountPrice: number;
  currency: string;
  currencySymbol: string;
};

/**
 * Gets the pricing information based on the country code
 *
 * @param countryCode - Two-letter country code (e.g., 'US', 'GB')
 * @returns PriceInfo object containing pricing details
 */
export const getSCOSPrice = (countryCode: string): PriceInfo => {
  return getCountrySpecificConfg(countryCode, {
    eu: {
      fullPrice: 140,
      discountPrice: 90,
      currency: "EUR",
      currencySymbol: "€",
    },
    us: {
      fullPrice: 150,
      discountPrice: 95,
      currency: "USD",
      currencySymbol: "$",
    },
    ca: {
      fullPrice: 200,
      discountPrice: 130,
      currency: "CAD",
      currencySymbol: "CA$",
    },
    gb: {
      fullPrice: 120,
      discountPrice: 80,
      currency: "GBP",
      currencySymbol: "£",
    },
    default: {
      fullPrice: 150,
      discountPrice: 95,
      currency: "USD",
      currencySymbol: "$",
    },
  });
};

export const getPixiPrice = (countryCode: string): PriceInfo => {
  return getCountrySpecificConfg(countryCode, {
    eu: {
      fullPrice: 79,
      discountPrice: 54,
      currency: "EUR",
      currencySymbol: "€",
    },
    us: {
      fullPrice: 65,
      discountPrice: 40,
      currency: "USD",
      currencySymbol: "$",
    },
    ca: {
      fullPrice: 97,
      discountPrice: 52,
      currency: "CAD",
      currencySymbol: "CA$",
    },
    gb: {
      fullPrice: 55,
      discountPrice: 35,
      currency: "GBP",
      currencySymbol: "£",
    },
    default: {
      fullPrice: 85,
      discountPrice: 60,
      currency: "USD",
      currencySymbol: "$",
    },
  });
};

export const getScosCouponCode = (countryCode: string): string => {
  return getCountrySpecificConfg(countryCode, {
    eu: "TRIAL50OFF",
    us: "TRIAL55OFF",
    ca: "TRIAL70OFF",
    gb: "TRIAL40OFF",
    default: "TRIAL55OFF",
  });
};
